import { NotificationType, type types } from "@fraction/shared";
import { useQueryClient } from "@tanstack/react-query";
import { memo, useCallback, useEffect, useRef } from "react";
import fraction from "src/api/fraction";
import { useAuthUser } from "src/auth/useAuth";
import { useAccountViewPreference } from "src/hooks/useAccountViewPreference";
import { useNotificationPreferences } from "src/hooks/useNotificationPreferences";
import { useWebsocketAuthenticated } from "src/hooks/useWebsocketAuthenticated";
import { useQuery } from "src/lib/react-query";
import { handleNotificationSideEffects, useNotificationStore } from "src/stores/notificationStore";
import { requestNotificationPermission, showBrowserNotification } from "./browserNotifications";
import { isHiddenFromNonEmployees } from "./notificationAudience";

const NOTIFICATIONS_TOPIC = "notifications";

/** Subscribes to the notifications WS topic and feeds messages into the Zustand store. Mount once at layout level. */
export const NotificationWsSubscription = memo(function NotificationWsSubscription() {
  const { token, user } = useAuthUser();
  const queryClient = useQueryClient();
  const addNotification = useNotificationStore((s) => s.addNotification);
  const { browserNotificationsEnabled } = useNotificationPreferences();
  const { preference } = useAccountViewPreference();
  const isEmployee = preference === "employee";
  const loadFromServer = useNotificationStore((s) => s.loadFromServer);
  const permissionRequested = useRef(false);

  // Fetch unread notifications from the server on load
  const fetchNotifications = useCallback(async () => {
    const result = await fraction.getNotifications({ limit: 50 });
    return result.data;
  }, []);

  // Every logged-in user reloads their own unread notifications, so a refresh
  // doesn't lose chat cards for brokers/lawyers. Keyed by user so switching
  // accounts in the same tab refetches.
  useQuery({
    queryKey: ["userNotifications", user?.id],
    queryFn: fetchNotifications,
    enabled: !!token && !!user?.id,
    onSuccess: (data) => {
      loadFromServer(data);
    },
  });

  const { lastMessage } = useWebsocketAuthenticated({
    routeKey: NOTIFICATIONS_TOPIC,
    enabled: !!token,
  });

  const handleMessage = useCallback(
    (msg: any) => {
      if (msg?.data?.id && msg.data.type) {
        // Echo of the user's own chat message (sent from another device or
        // tab): update the chat cache so the open chat stays live, but don't
        // surface a notification for their own message.
        if (
          msg.data.type === NotificationType.CHAT_MESSAGE_RECEIVED &&
          (msg.data as types.ChatMessageNotification).chatData?.userId === user?.id
        ) {
          handleNotificationSideEffects(msg.data, queryClient);
          return;
        }

        if (!isEmployee && isHiddenFromNonEmployees(msg.data)) {
          return;
        }

        addNotification(msg.data, queryClient);

        // Opting out of OS notifications also means we never prompt for the
        // permission — the store still receives everything either way.
        if (!browserNotificationsEnabled) {
          return;
        }

        // Request permission on first notification (non-intrusive — only prompts once)
        if (!permissionRequested.current) {
          permissionRequested.current = true;
          requestNotificationPermission();
        }

        showBrowserNotification(msg.data);
      }
    },
    [addNotification, queryClient, user?.id, browserNotificationsEnabled, isEmployee]
  );

  useEffect(() => {
    if (lastMessage) {
      handleMessage(lastMessage);
    }
  }, [lastMessage, handleMessage]);

  // Pre-request permission when authenticated so notifications are ready
  useEffect(() => {
    if (!!token && browserNotificationsEnabled && !permissionRequested.current) {
      permissionRequested.current = true;
      requestNotificationPermission();
    }
  }, [token, browserNotificationsEnabled]);

  return null;
});

NotificationWsSubscription.displayName = "NotificationWsSubscription";
